import { useState, useRef, useCallback, useEffect } from "react"
import type { Script, SceneAsset, Listing, Account } from "@/types"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { OptimizedImage } from "@/components/ui/optimized-image"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { ScriptContent, TextPosition, TextAlign } from "./storyboard/constants"
import {
  MOTION_PRESETS,
  MOTION_CATEGORIES,
  DEFAULT_TITLE_CARD_DURATION,
  MIN_CLIP_DURATION,
  GOOGLE_FONTS_URL,
  MAX_CONCURRENT_ANIMATIONS,
} from "./storyboard/constants"
import { FilmIcon } from "./storyboard/icons"
import PreviewPanel from "./storyboard/preview-panel"
import Timeline from "./storyboard/timeline"
import IntroOfferModal from "@/components/dashboard/intro-offer-modal"

const EMPTY_SCENES: NonNullable<ScriptContent["scenes"]> = []

export default function WizardStepStoryboard({
  scripts,
  aspectRatio = "landscape",
  templateVariant = "classic",
  imageUrls = [],
  allImagesData = [],
  onSwapSceneImage,
  onUpdateScript,
  sceneAssets = [],
  sceneAssetStatuses = {},
  onEnhanceScene,
  onAnimateScene,
  onRevertScene,
  voiceoverCompleted,
  hasMusic = false,
  musicUrl,
  musicVolume = 0.3,
  onMusicVolumeChange,
  voiceoverVolume = 1.0,
  onVoiceoverVolumeChange,
  listing,
  account,
}: {
  scripts: Script[]
  aspectRatio?: "portrait" | "landscape" | "square"
  templateVariant?: string
  imageUrls?: string[]
  allImagesData?: Array<{ signed_id: string; url: string; thumb_url: string; description?: string }>
  onSwapSceneImage?: (sceneIndex: number, signedId: string) => void
  onUpdateScript: (content: Record<string, unknown>) => void
  sceneAssets?: SceneAsset[]
  sceneAssetStatuses?: Record<string, { status: SceneAsset["status"]; message?: string; progress?: number }>
  onEnhanceScene?: (sceneIndex: number) => void
  onAnimateScene?: (sceneIndex: number, motionPrompt?: string) => void
  onRevertScene?: (sceneIndex: number) => void
  voiceoverCompleted: boolean
  hasMusic?: boolean
  musicUrl?: string
  musicVolume?: number
  onMusicVolumeChange?: (volume: number) => void
  voiceoverVolume?: number
  onVoiceoverVolumeChange?: (volume: number) => void
  listing: Listing
  account: Account
}) {
  const activeScript = scripts.find((s) => s.active)
  const serverContent = activeScript?.content as ScriptContent | undefined
  const [localContent, setLocalContent] = useState<ScriptContent | null>(null)
  const [selectedSceneIndex, setSelectedSceneIndex] = useState(-1)
  const [imagePickerScene, setImagePickerScene] = useState<number | null>(null)
  const [animateDialogScene, setAnimateDialogScene] = useState<number | null>(null)
  const [selectedPreset, setSelectedPreset] = useState<string>(MOTION_PRESETS[0].id)
  const [customPrompt, setCustomPrompt] = useState("")
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [animateTipDismissed, setAnimateTipDismissed] = useState(() => {
    if (typeof window === "undefined") return false
    return localStorage.getItem("quiteright:storyboard:animate-tip-dismissed") === "true"
  })

  function dismissAnimateTip() {
    setAnimateTipDismissed(true)
    localStorage.setItem("quiteright:storyboard:animate-tip-dismissed", "true")
  }

  // Audio playback state
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const musicRef = useRef<HTMLAudioElement | null>(null)
  const [playingScene, setPlayingScene] = useState<number | null>(null)
  const [playAllMode, setPlayAllMode] = useState(false)
  const playEndRef = useRef<number>(0)
  const [audioCurrentTime, setAudioCurrentTime] = useState(0)
  const rafRef = useRef<number | null>(null)

  const voiceoverUrl = activeScript?.voiceover_url
  const serverVoiceoverDuration = activeScript?.voiceover_duration
  const serverDurationValid = !!serverVoiceoverDuration && serverVoiceoverDuration > 0
  const [clientDuration, setClientDuration] = useState<number | null>(null)

  // Client-side fallback: measure audio duration when server returns 0 or missing
  useEffect(() => {
    if (serverDurationValid || !voiceoverUrl) return
    const audio = new Audio(voiceoverUrl)
    function onLoaded() {
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        setClientDuration(audio.duration)
      }
    }
    audio.addEventListener("loadedmetadata", onLoaded)
    return () => {
      audio.removeEventListener("loadedmetadata", onLoaded)
      audio.src = ""
    }
  }, [voiceoverUrl, serverDurationValid])

  const voiceoverDuration = serverDurationValid
    ? serverVoiceoverDuration
    : clientDuration ?? serverVoiceoverDuration

  const content = localContent ?? serverContent

  const debouncedSave = useCallback(
    (updated: ScriptContent) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      saveTimerRef.current = setTimeout(() => {
        onUpdateScript(updated as Record<string, unknown>)
      }, 800)
    },
    [onUpdateScript],
  )

  // Compute scene arrays and totals
  const contentScenes = content?.scenes?.filter((s) => s.type !== "closing") ?? EMPTY_SCENES
  const closingScene = content?.scenes?.find((s) => s.type === "closing")
  const closingDuration = closingScene?.duration_seconds ?? 4

  // --- Audio playback ---
  const titleDuration = content?.title_card_duration ?? DEFAULT_TITLE_CARD_DURATION

  // Track whether music was started so the tick loop can sync it
  const musicStartedRef = useRef(false)

  function playAll() {
    if (!audioRef.current || !voiceoverUrl) return
    const audio = audioRef.current
    audio.currentTime = 0
    audio.volume = voiceoverVolume
    playEndRef.current = Infinity
    setPlayAllMode(true)
    setPlayingScene(-1)
    setSelectedSceneIndex(-1)
    musicStartedRef.current = false
    audio.play().then(() => {
      // Start music once voiceover is confirmed playing
      startMusic()
    })
  }

  function startMusic() {
    const music = musicRef.current
    if (!music || musicStartedRef.current) return
    music.currentTime = 0
    music.volume = musicVolume
    music.play().then(() => {
      musicStartedRef.current = true
    }).catch(() => {
      // Browser blocked play — the tick loop will retry
    })
  }

  function stopPlayback() {
    if (audioRef.current) {
      audioRef.current.pause()
    }
    if (musicRef.current) {
      musicRef.current.pause()
    }
    musicStartedRef.current = false
    setPlayingScene(null)
    setPlayAllMode(false)
  }

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    function findSceneForTime(time: number): number {
      if (time < titleDuration) return -1
      let elapsed = titleDuration
      for (let i = 0; i < contentScenes.length; i++) {
        elapsed += contentScenes[i].duration_seconds
        if (time < elapsed) return i
      }
      return contentScenes.length
    }

    function tick() {
      if (!audio) return
      // Bail out if audio was paused (stopPlayback was called between RAF schedule and execution)
      if (audio.paused) return
      setAudioCurrentTime(audio.currentTime)

      // Ensure music is playing — retry if initial start was deferred
      if (!musicStartedRef.current && musicRef.current) {
        const music = musicRef.current
        music.currentTime = audio.currentTime
        music.volume = musicVolume
        music.play().then(() => {
          musicStartedRef.current = true
        }).catch(() => { /* will retry next tick */ })
      }

      // In play-all mode, auto-advance the selected scene
      if (playAllMode) {
        const currentScene = findSceneForTime(audio.currentTime)
        setSelectedSceneIndex(currentScene)
        setPlayingScene(currentScene)
      }

      if (audio.currentTime >= playEndRef.current) {
        audio.pause()
        if (musicRef.current) musicRef.current.pause()
        musicStartedRef.current = false
        setPlayingScene(null)
        setPlayAllMode(false)
        return
      }
      rafRef.current = requestAnimationFrame(tick)
    }

    if (playingScene !== null) {
      rafRef.current = requestAnimationFrame(tick)
    }

    function handleEnded() {
      if (musicRef.current) musicRef.current.pause()
      musicStartedRef.current = false
      setPlayingScene(null)
      setPlayAllMode(false)
    }
    audio.addEventListener("ended", handleEnded)

    return () => {
      audio.removeEventListener("ended", handleEnded)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [playingScene, playAllMode, titleDuration, contentScenes, musicVolume])

  // Sync music volume to audio element
  useEffect(() => {
    if (musicRef.current) musicRef.current.volume = musicVolume
  }, [musicVolume])

  // Sync voiceover volume to audio element
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = voiceoverVolume
  }, [voiceoverVolume])

  // --- Duration update with adjacent balancing ---
  function updateSceneDuration(index: number, newDuration: number) {
    if (!content?.scenes) return
    const contentScenes = content.scenes.filter((s) => s.type !== "closing")
    const closingScenes = content.scenes.filter((s) => s.type === "closing")
    if (index >= contentScenes.length) return

    const oldDuration = contentScenes[index].duration_seconds
    const delta = newDuration - oldDuration

    // Find adjacent scene to compensate
    const adjacentIndex = index < contentScenes.length - 1 ? index + 1 : index - 1
    if (adjacentIndex < 0 || adjacentIndex >= contentScenes.length) {
      const updated = [...contentScenes]
      updated[index] = { ...updated[index], duration_seconds: newDuration }
      const newContent = { ...content, scenes: [...updated, ...closingScenes] }
      setLocalContent(newContent)
      debouncedSave(newContent)
      return
    }

    const adjacentDuration = contentScenes[adjacentIndex].duration_seconds - delta
    const clampedAdjacent = Math.max(MIN_CLIP_DURATION, adjacentDuration)
    const actualNewDuration = newDuration - (clampedAdjacent - Math.max(MIN_CLIP_DURATION, adjacentDuration))

    const updated = contentScenes.map((s, i) => {
      if (i === index) return { ...s, duration_seconds: Math.round(actualNewDuration * 10) / 10 }
      if (i === adjacentIndex) return { ...s, duration_seconds: Math.round(clampedAdjacent * 10) / 10 }
      return s
    })

    const newContent = { ...content, scenes: [...updated, ...closingScenes] }
    setLocalContent(newContent)
    debouncedSave(newContent)
  }

  // Resize handler for timeline clips (including title and closing)
  function handleResizeClip(leftIndex: number, leftType: string, delta: number) {
    if (!content?.scenes) return
    const contentScenes = content.scenes.filter((s) => s.type !== "closing")
    const closingScenes = content.scenes.filter((s) => s.type === "closing")

    if (leftType === "title") {
      // Resize title ↔ first content scene
      const newTitleDur = Math.max(MIN_CLIP_DURATION, Math.round((titleDuration + delta) * 10) / 10)
      const firstScene = contentScenes[0]
      if (!firstScene) return
      const newFirstDur = Math.max(MIN_CLIP_DURATION, Math.round((firstScene.duration_seconds - delta) * 10) / 10)
      const updatedScenes = contentScenes.map((s, i) =>
        i === 0 ? { ...s, duration_seconds: newFirstDur } : s,
      )
      const newContent = { ...content, title_card_duration: newTitleDur, scenes: [...updatedScenes, ...closingScenes] }
      setLocalContent(newContent)
      debouncedSave(newContent)
    } else if (leftType === "content") {
      // Check if right neighbour is closing
      const rightIsClosing = leftIndex === contentScenes.length - 1
      if (rightIsClosing && closingScenes.length > 0) {
        // Resize last content scene ↔ closing
        const leftScene = contentScenes[leftIndex]
        const closingScene = closingScenes[0]
        const newLeftDur = Math.max(MIN_CLIP_DURATION, Math.round((leftScene.duration_seconds + delta) * 10) / 10)
        const newClosingDur = Math.max(MIN_CLIP_DURATION, Math.round((closingScene.duration_seconds - delta) * 10) / 10)
        const updatedContent = contentScenes.map((s, i) =>
          i === leftIndex ? { ...s, duration_seconds: newLeftDur } : s,
        )
        const updatedClosing = closingScenes.map((s, i) =>
          i === 0 ? { ...s, duration_seconds: newClosingDur } : s,
        )
        const newContent = { ...content, scenes: [...updatedContent, ...updatedClosing] }
        setLocalContent(newContent)
        debouncedSave(newContent)
      } else {
        // Content scene ↔ content scene
        updateSceneDuration(leftIndex, contentScenes[leftIndex].duration_seconds + delta)
      }
    }
  }

  // Compute scene totals for display
  const scenesTotal = contentScenes.reduce((sum, s) => sum + s.duration_seconds, 0)
  const totalWithTitle = titleDuration + scenesTotal + closingDuration

  function updateScene(index: number, text: string) {
    if (!content?.scenes) return
    const updated = content.scenes.map((s, i) =>
      i === index ? { ...s, text } : s,
    )
    const newContent = { ...content, scenes: updated }
    setLocalContent(newContent)
    debouncedSave(newContent)
  }

  function updateHeadline(text: string) {
    const newContent = { ...content, headline: text }
    setLocalContent(newContent)
    debouncedSave(newContent)
  }

  function updateTitleCardBg(bg: { colour?: string }) {
    const newContent: ScriptContent = {
      ...content,
      title_card_bg_colour: bg.colour ?? content?.title_card_bg_colour,
    }
    setLocalContent(newContent)
    debouncedSave(newContent)
  }

  function updateSceneStyle(index: number, style: { text_position?: TextPosition; text_align?: TextAlign; text_font?: string; text_size?: string; text_colour?: string; text_uppercase?: boolean; text_weight?: string; show_cta?: boolean; show_cta_button?: boolean; cta_button_text_colour?: string }) {
    if (!content?.scenes) return
    const { cta_button_text_colour, ...sceneStyle } = style
    const updated = content.scenes.map((s, i) =>
      i === index ? { ...s, ...sceneStyle } : s,
    )
    const newContent = {
      ...content,
      scenes: updated,
      ...(cta_button_text_colour !== undefined ? { cta_button_text_colour } : {}),
    }
    setLocalContent(newContent)
    debouncedSave(newContent)
  }

  function updateTitleStyle(style: { text_position?: TextPosition; text_align?: TextAlign; text_font?: string; text_size?: string; text_colour?: string; text_uppercase?: boolean; text_weight?: string; show_branding?: boolean; title_bg_gradient?: ScriptContent["title_bg_gradient"]; title_bg_gradient_end?: string; title_logo_position?: ScriptContent["title_logo_position"]; title_logo_size?: ScriptContent["title_logo_size"]; title_transition_style?: ScriptContent["title_transition_style"] }) {
    const newContent: ScriptContent = {
      ...content,
      title_text_position: style.text_position ?? content?.title_text_position,
      title_text_align: style.text_align ?? content?.title_text_align,
      title_text_font: style.text_font ?? content?.title_text_font,
      title_text_size: style.text_size ?? content?.title_text_size,
      title_text_colour: style.text_colour ?? content?.title_text_colour,
      title_text_uppercase: style.text_uppercase ?? content?.title_text_uppercase,
      title_text_weight: style.text_weight ?? content?.title_text_weight,
      title_show_branding: style.show_branding ?? content?.title_show_branding,
      title_bg_gradient: style.title_bg_gradient ?? content?.title_bg_gradient,
      title_bg_gradient_end: style.title_bg_gradient_end ?? content?.title_bg_gradient_end,
      title_logo_position: style.title_logo_position ?? content?.title_logo_position,
      title_logo_size: style.title_logo_size ?? content?.title_logo_size,
      title_transition_style: style.title_transition_style ?? content?.title_transition_style,
    }
    setLocalContent(newContent)
    debouncedSave(newContent)
  }

  function updateClosingStyle(style: { closing_bg_colour?: string; closing_bg_gradient?: ScriptContent["closing_bg_gradient"]; closing_bg_gradient_end?: string; closing_transition_style?: ScriptContent["closing_transition_style"]; closing_text_font?: string; closing_text_size?: string; closing_text_weight?: string; closing_text_colour?: string; closing_text_uppercase?: boolean; closing_logo_size?: ScriptContent["closing_logo_size"]; show_watermark?: boolean }) {
    const newContent = {
      ...content,
      ...style,
    }
    setLocalContent(newContent)
    debouncedSave(newContent)
  }

  function applyStyleToAllScenes(style: { text_position?: TextPosition; text_align?: TextAlign; text_font?: string; text_size?: string; text_colour?: string; text_uppercase?: boolean; text_weight?: string; show_cta?: boolean; show_cta_button?: boolean; cta_button_text_colour?: string }) {
    if (!content?.scenes) return
    const { cta_button_text_colour, ...sceneStyle } = style
    const updated = content.scenes.map((s) =>
      s.type === "closing" ? s : { ...s, ...sceneStyle },
    )
    const newContent = {
      ...content,
      scenes: updated,
      ...(cta_button_text_colour !== undefined ? { cta_button_text_colour } : {}),
    }
    setLocalContent(newContent)
    debouncedSave(newContent)
  }

  // Load Google Fonts for preview
  useEffect(() => {
    if (document.querySelector("link[data-google-fonts]")) return
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = GOOGLE_FONTS_URL
    link.dataset.googleFonts = "true"
    document.head.appendChild(link)
  }, [])

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return
      if (e.key === "ArrowLeft") {
        e.preventDefault()
        setSelectedSceneIndex((prev) => Math.max(-1, prev - 1))
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        setSelectedSceneIndex((prev) => Math.min(contentScenes.length, prev + 1))
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [contentScenes.length])

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="mb-1 font-display text-2xl font-semibold text-charcoal">
            Storyboard
          </h2>
          <p className="text-sm text-warm-grey sm:text-base">
            Enhance photos, animate scenes, and edit overlay text for each scene.
          </p>
        </div>

        {/* Play-all + timing summary */}
        {voiceoverCompleted && voiceoverDuration != null && contentScenes.length > 0 && (
          <div className="flex shrink-0 items-center gap-3">
            <Button
              size="sm"
              onClick={playAllMode ? stopPlayback : playAll}
              className={playAllMode
                ? "bg-charcoal text-white hover:bg-charcoal/80"
                : "bg-red text-white hover:bg-deep-red"
              }
            >
              {playAllMode ? (
                <>
                  <svg className="size-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                  Pause
                </>
              ) : (
                <>
                  <svg className="size-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  Play all
                </>
              )}
            </Button>
            <div className="flex items-center gap-2 text-[11px] text-warm-grey">
              <svg className="size-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              <span>
                Voiceover: {voiceoverDuration.toFixed(1)}s / Scenes: {totalWithTitle.toFixed(1)}s
              </span>
              {Math.abs(totalWithTitle - voiceoverDuration) > 0.5 && (
                <span className="rounded bg-amber-100 px-1 py-px text-[9px] font-medium text-amber-700">
                  {totalWithTitle > voiceoverDuration ? "+" : ""}{(totalWithTitle - voiceoverDuration).toFixed(1)}s
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Animate tip — only on content scenes (title/closing can't be animated) */}
      {voiceoverCompleted && !animateTipDismissed && selectedSceneIndex >= 0 && selectedSceneIndex < contentScenes.length && (() => {
        const animatingCount = Object.entries(sceneAssetStatuses).filter(
          ([key, value]) => key.endsWith("-animated_clip") && value.status === "generating",
        ).length
        const capReached = animatingCount >= MAX_CONCURRENT_ANIMATIONS
        return (
          <div className="mb-4 flex items-start gap-3 rounded-lg border border-stone/30 bg-cream/40 px-4 py-3">
            <div className="relative shrink-0">
              <span
                className="absolute inset-0 animate-ping rounded-full bg-red/20"
                style={{ animationIterationCount: 1, animationDuration: "1.2s", animationFillMode: "forwards" }}
                aria-hidden="true"
              />
              <div className="relative flex size-10 items-center justify-center rounded-full bg-red/10 text-red">
                <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                </svg>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-charcoal">
                Bring your scenes to life with Animate
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-warm-grey">
                Select any scene and hit <span className="font-semibold text-charcoal">Animate</span> to add subtle camera motion — orbits, walk-throughs, pan-ups. Up to {MAX_CONCURRENT_ANIMATIONS} clips can animate at once.
              </p>
            </div>
            {animatingCount > 0 && (
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums ${
                capReached ? "bg-red/10 text-red" : "bg-charcoal/5 text-charcoal"
              }`}>
                {animatingCount}/{MAX_CONCURRENT_ANIMATIONS} animating
              </span>
            )}
            <button
              type="button"
              onClick={dismissAnimateTip}
              className="shrink-0 rounded p-1 text-warm-grey transition-colors hover:bg-stone/20 hover:text-charcoal"
              aria-label="Dismiss tip"
              title="Don't show again"
            >
              <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )
      })()}

      {/* Voiceover gate */}
      {!voiceoverCompleted && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <svg className="mt-0.5 size-5 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-amber-800">
              Voiceover needed for accurate timing
            </p>
            <p className="mt-0.5 text-xs text-amber-700">
              Generate a voiceover in the Audio step first to unlock scene timing. Animations will use the voiceover-derived durations for each scene.
            </p>
          </div>
        </div>
      )}

      {/* Hidden audio elements */}
      {voiceoverUrl && <audio ref={audioRef} src={voiceoverUrl} preload="auto" className="hidden" />}
      {musicUrl && <audio ref={musicRef} src={musicUrl} preload="auto" className="hidden" />}

      {/* Preload animated clip videos so browser cache is warm during play-all transitions */}
      {sceneAssets
        .filter((sa) => sa.asset_type === "animated_clip" && sa.status === "completed" && sa.file_url)
        .map((sa) => (
          <video
            key={`preload-${sa.scene_index}`}
            src={sa.file_url!}
            preload="auto"
            muted
            playsInline
            className="pointer-events-none fixed -left-[9999px] size-0 opacity-0"
          />
        ))
      }

      {/* NLE layout: Preview + Inspector on top, Timeline below */}
      {content?.scenes && content.scenes.length > 0 ? (
        <div className="space-y-3">
          {/* Preview panel */}
          <PreviewPanel
            selectedIndex={selectedSceneIndex}
            content={content}
            aspectRatio={aspectRatio}
            templateVariant={templateVariant}
            imageUrls={imageUrls}
            sceneAssets={sceneAssets}
            sceneAssetStatuses={sceneAssetStatuses}
            voiceoverCompleted={voiceoverCompleted}
            listing={listing}
            account={account}
            isPlaying={playingScene !== null && (playAllMode || playingScene === selectedSceneIndex)}
            onPlayAll={playAll}
            onStopPlayback={stopPlayback}
            onUpdateScene={updateScene}
            onUpdateHeadline={updateHeadline}
            onUpdateTitleCardBg={updateTitleCardBg}
            onEnhanceScene={(i) => onEnhanceScene?.(i)}
            onAnimateScene={(i) => {
              setAnimateDialogScene(i)
              setSelectedPreset(MOTION_PRESETS[0].id)
              setCustomPrompt("")
            }}
            onSwapImage={(i) => setImagePickerScene(i)}
            onRevertScene={(i) => onRevertScene?.(i)}
            onUpdateSceneStyle={updateSceneStyle}
            onUpdateTitleStyle={updateTitleStyle}
            onUpdateClosingStyle={updateClosingStyle}
            onApplyStyleToAll={applyStyleToAllScenes}
          />

          {/* Timeline */}
          <Timeline
            content={content}
            imageUrls={imageUrls}
            brandColour={account.brand_colour || "#C41E2A"}
            sceneAssets={sceneAssets}
            sceneAssetStatuses={sceneAssetStatuses}
            selectedIndex={selectedSceneIndex}
            onSelect={setSelectedSceneIndex}
            audioCurrentTime={audioCurrentTime}
            isPlaying={playingScene !== null}
            hasVoiceover={!!voiceoverUrl && voiceoverCompleted}
            hasMusic={hasMusic}
            musicVolume={musicVolume}
            onMusicVolumeChange={onMusicVolumeChange}
            voiceoverVolume={voiceoverVolume}
            onVoiceoverVolumeChange={onVoiceoverVolumeChange}
            onResizeClip={handleResizeClip}
          />
        </div>
      ) : (
        <div className="flex flex-col items-center rounded-lg border border-stone/20 bg-cream/30 py-12 text-center">
          <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-stone/20">
            <svg className="size-6 text-warm-grey" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
            </svg>
          </div>
          <p className="text-sm text-warm-grey">
            Generate a script first to see your scenes here.
          </p>
        </div>
      )}

      {/* Scene image picker dialog */}
      <Dialog
        open={imagePickerScene !== null}
        onOpenChange={(open) => { if (!open) setImagePickerScene(null) }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Change photo — Scene {imagePickerScene !== null ? imagePickerScene + 1 : ""}
            </DialogTitle>
            <DialogDescription>
              Pick any photo from this listing.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2">
            {allImagesData.map((img) => {
              const isActive = imagePickerScene !== null && imageUrls[imagePickerScene] === img.url
              return (
                <button
                  key={img.signed_id}
                  type="button"
                  onClick={() => {
                    if (imagePickerScene !== null && onSwapSceneImage) {
                      onSwapSceneImage(imagePickerScene, img.signed_id)
                    }
                    setImagePickerScene(null)
                  }}
                  className={`relative overflow-hidden rounded-lg border-2 transition-all ${
                    isActive
                      ? "border-red ring-2 ring-red/20"
                      : "border-stone/30 hover:border-stone"
                  }`}
                >
                  <OptimizedImage
                    src={img.thumb_url}
                    alt=""
                    className="aspect-[4/3] w-full object-cover"
                  />
                  {isActive && (
                    <div className="absolute inset-0 flex items-center justify-center bg-red/10">
                      <svg className="size-5 text-red" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                      </svg>
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox preview dialog (from timeline double-click, kept for future use) */}

      {/* Animate prompt dialog */}
      <Dialog
        open={animateDialogScene !== null}
        onOpenChange={(open) => { if (!open) setAnimateDialogScene(null) }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Camera motion — Scene {animateDialogScene !== null ? animateDialogScene + 1 : ""}
            </DialogTitle>
            <DialogDescription>
              Choose a camera movement style or write your own prompt.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Preset options grouped by category */}
            {MOTION_CATEGORIES.map((category) => (
              <div key={category}>
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-warm-grey">{category}</p>
                <div className="space-y-1.5">
                  {MOTION_PRESETS.filter((p) => p.category === category).map((preset) => (
                    <label
                      key={preset.id}
                      className={`flex cursor-pointer items-start gap-2.5 rounded-lg border bg-white px-3 py-2.5 transition-all ${
                        selectedPreset === preset.id
                          ? "border-red/40 ring-1 ring-red/20"
                          : "border-stone/30 hover:border-stone/50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="motion-preset"
                        value={preset.id}
                        checked={selectedPreset === preset.id}
                        onChange={() => setSelectedPreset(preset.id)}
                        className="sr-only"
                      />
                      <div className={`mt-0.5 flex size-3.5 shrink-0 items-center justify-center rounded-full border-2 ${
                        selectedPreset === preset.id ? "border-red" : "border-stone/40"
                      }`}>
                        {selectedPreset === preset.id && <div className="size-1.5 rounded-full bg-red" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-charcoal">{preset.label}</p>
                        <p className="text-[11px] leading-snug text-warm-grey">{preset.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            {/* Custom option */}
            <label
              className={`flex cursor-pointer items-start gap-3 rounded-lg border bg-white px-3 py-2.5 transition-all ${
                selectedPreset === "custom"
                  ? "border-red/40 ring-1 ring-red/20"
                  : "border-stone/30 hover:border-stone/50"
              }`}
            >
              <input
                type="radio"
                name="motion-preset"
                value="custom"
                checked={selectedPreset === "custom"}
                onChange={() => setSelectedPreset("custom")}
                className="sr-only"
              />
              <div className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border-2 ${
                selectedPreset === "custom" ? "border-red" : "border-stone/40"
              }`}>
                {selectedPreset === "custom" && <div className="size-2 rounded-full bg-red" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-charcoal">Custom prompt</p>
                {selectedPreset === "custom" && (
                  <Textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="Describe the camera movement you want..."
                    className="mt-2 min-h-[60px] resize-none text-sm"
                    rows={2}
                    autoFocus
                  />
                )}
              </div>
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAnimateDialogScene(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-red text-white hover:bg-deep-red"
              disabled={selectedPreset === "custom" && !customPrompt.trim()}
              onClick={() => {
                if (animateDialogScene === null) return
                let prompt: string | undefined
                if (selectedPreset === "custom") {
                  prompt = customPrompt.trim()
                } else {
                  prompt = MOTION_PRESETS.find((p) => p.id === selectedPreset)?.prompt
                }
                onAnimateScene?.(animateDialogScene, prompt)
                setAnimateDialogScene(null)
              }}
            >
              <FilmIcon />
              Animate
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <IntroOfferModal />
    </div>
  )
}
